/**
 * API Key Storage Utility
 *
 * Retrieves API key (OpenAI or Google) from backend API only (requires authentication).
 *
 * Security design:
 * - Single source of truth: Backend API (/api/agent/gk)
 * - No fallback storage locations to eliminate attack vectors
 * - Key held in memory only, never persisted
 * - Requires valid JWT token for retrieval
 */

import { decryptApiKey } from './ak-encryption'
import { handleUnauthorized, logApiError } from './api-error-handler'
import { API_URL } from './config'
import { getAuthToken } from './supabase'

// In-memory cache for pre-fetched API keys
const gkCache = new Map<string, { key: string; expiresAt: number }>()
const GK_CACHE_TTL_MS = 5 * 60 * 1000 // 5 minutes

/**
 * Pre-fetch and cache the API key so it's ready when the user sends their first message.
 * Errors are silently swallowed — this is a best-effort optimisation.
 */
export async function prefetchGk(model: string): Promise<void> {
  const cached = gkCache.get(model)
  if (cached && cached.expiresAt > Date.now()) return

  try {
    const key = await gk(model)
    if (key) {
      gkCache.set(model, { key, expiresAt: Date.now() + GK_CACHE_TTL_MS })
    }
  } catch {
    // Best-effort — don't block the UI
  }
}

/**
 * Clear the pre-fetch cache (e.g. on auth change).
 */
export function clearGkCache(): void {
  gkCache.clear()
}

/**
 * Get the API key from backend (requires authentication)
 *
 * Security notes:
 * - Key is held in memory only (not persisted to localStorage)
 * - Expires when browser/tab closes
 * - Requires valid JWT token
 * - Automatically retries once with refreshed token on 401
 *
 * @param model - Model name to determine which API key to retrieve
 * @returns Shared API key or null if not found
 */
export async function gk(model: string): Promise<string | null> {
  // Check in-memory cache first
  const cached = gkCache.get(model)
  if (cached && cached.expiresAt > Date.now()) {
    return cached.key
  }

  const token = await getAuthToken()
  if (!token) {
    console.warn('No auth token available')
    return null
  }

  const url = new URL(`${API_URL}/api/agent/gk`)
  url.searchParams.set('model', model)

  let response: Response
  try {
    response = await fetch(url.toString(), {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        'ngrok-skip-browser-warning': 'true',
      },
    })
  } catch (error) {
    logApiError('AK error', error)
    throw error
  }

  if (response.ok) {
    const data = await response.json()
    if (data.ak) {
      // Decrypt the API key using the JWT token
      const decryptedKey = await decryptApiKey(data.ak, token)
      gkCache.set(model, {
        key: decryptedKey,
        expiresAt: Date.now() + GK_CACHE_TTL_MS,
      })
      return decryptedKey
    }
    return null
  }

  if (response.status === 402) {
    const errorData = await response.json().catch(() => ({}))
    const message =
      (errorData as { error?: string }).error ||
      'Insufficient credits. Please top up to continue.'
    throw new Error(message)
  }

  if (response.status === 401) {
    // Token expired or invalid - try refreshing and retry once
    console.warn('API key request returned 401, attempting token refresh...')
    const refreshedToken = await getAuthToken()

    if (refreshedToken && refreshedToken !== token) {
      // Retry with refreshed token
      const retryResponse = await fetch(url.toString(), {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${refreshedToken}`,
          'ngrok-skip-browser-warning': 'true',
        },
      })

      if (retryResponse.ok) {
        const data = await retryResponse.json()
        if (data.ak) {
          // Decrypt the API key using the refreshed JWT token
          const decryptedKey = await decryptApiKey(data.ak, refreshedToken)
          gkCache.set(model, {
            key: decryptedKey,
            expiresAt: Date.now() + GK_CACHE_TTL_MS,
          })
          return decryptedKey
        }
        return null
      }

      if (retryResponse.status === 401) {
        // Refresh also failed - sign out
        await handleUnauthorized(retryResponse, 'Get API key')
        return null
      }

      if (retryResponse.status === 402) {
        const errorData = await retryResponse.json().catch(() => ({}))
        const message =
          (errorData as { error?: string }).error ||
          'Insufficient credits. Please top up to continue.'
        throw new Error(message)
      }

      const errorData = await retryResponse.json().catch(() => ({}))
      console.error('Failed to fetch API key after token refresh:', errorData)
      return null
    }

    // Token refresh unavailable - sign out
    await handleUnauthorized(response, 'Get API key')
    return null
  }

  const errorData = await response.json().catch(() => ({}))
  console.error('Failed to fetch API key:', errorData)
  return null
}
